import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sprout, Building2, Factory } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { t } from "../lib/i18n";
import { CText } from "../components/CText";
import { PropertyCard } from "../components/PropertyCard";

const HERO_BG = "/assets/hero-bg-v16.jpg";
const CAT_IMG = {
  rural: "https://images.unsplash.com/photo-1721674588856-8966d18bcd0f?w=1600",
  urban: "https://images.unsplash.com/photo-1758304480372-2f5e11323aeb?w=1600",
  industrial: "https://images.unsplash.com/photo-1758563440179-24c1bcc28d62?w=1600",
};

export default function Home() {
  const { lang } = useLanguage();
  const { get } = useContent();
  const [properties, setProperties] = useState([]);

  useEffect(() => { api.get("/properties").then(({ data }) => setProperties(data)); }, []);
  const featured = properties.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO — sits BELOW the fixed header with extra breathing room */}
      <section
        className="relative w-full overflow-hidden flex items-start pt-8 md:pt-14 pb-12 md:pb-16 mt-[calc(var(--header-h,7rem)+0.5rem)] min-h-[560px] md:min-h-[480px] md:aspect-[16/9] md:max-h-[820px]"
        data-testid="hero-section"
      >
        {/* Background image fills the section, anchored to TOP so the sky is always visible */}
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "60% center" }}
        />
        {/* Stronger dark overlay so the image stays subtle */}
        <div className="absolute inset-0 pointer-events-none bg-[#0A0F16]/55" />
        {/* Bottom gradient for text legibility */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none bg-gradient-to-t from-[#0A0F16] via-[#0A0F16]/40 to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: [0.2, 0.8, 0.2, 1] }}>
            <CText section="home" field="hero_overline" as="div" className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-4 md:mb-6" data-testid="hero-overline" />
            <CText section="home" field="hero_title" as="h1" className="font-serif-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white max-w-4xl mb-6 md:mb-10" data-testid="hero-title" />
            <CText section="home" field="hero_sub" as="p" className="text-[#A3A9B2] text-sm md:text-lg max-w-2xl mb-8 md:mb-12 leading-relaxed" />
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/gallery" className="inline-flex items-center gap-3 bg-[#C5A059] text-[#0A0F16] px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#E5C07B] transition-all group" data-testid="hero-explore-btn">
                {t(lang, "hero_cta")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-3 border border-[#C5A059]/40 text-white px-8 py-4 text-sm tracking-[0.2em] uppercase hover:border-[#C5A059] hover:bg-[#C5A059]/10 transition-all" data-testid="hero-contact-btn">
                {t(lang, "hero_cta_alt")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { key: "rural", icon: Sprout, img: CAT_IMG.rural },
              { key: "urban", icon: Building2, img: CAT_IMG.urban },
              { key: "industrial", icon: Factory, img: CAT_IMG.industrial },
            ].map((c, i) => (
              <motion.div key={c.key} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.8, delay: i * 0.12 }}
                data-testid={`category-card-${c.key}`}>
                <Link to={`/gallery?cat=${c.key}`} className="block group">
                  <div className="relative aspect-[4/5] overflow-hidden mb-4">
                    <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F16]/90 to-transparent" />
                    <div className="absolute top-6 left-6"><c.icon size={24} className="text-[#C5A059]" /></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <CText section="home" field={`cat_${c.key}_title`} as="h3" className="font-serif-display text-3xl md:text-4xl text-white mb-2" />
                      <CText section="home" field={`cat_${c.key}_desc`} as="p" className="text-[#A3A9B2] text-sm leading-relaxed" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROPERTIES */}
      {featured.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-[#C5A059]/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
              <div>
                <CText section="home" field="featured_overline" as="div" className="text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-4" />
                <CText section="home" field="featured_title" as="h2" className="font-serif-display text-4xl md:text-5xl text-white max-w-2xl leading-tight" />
              </div>
              <Link to="/gallery" className="link-copper text-sm tracking-[0.2em] uppercase text-[#C5A059]">{t(lang, "view_details")} →</Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {featured.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
